import { COLUMNS } from './keys';
import { query } from './query';
import DataTable from '../../../../components/Datatable';
import { getDifference } from '../../../../helpers/showDelta';
import Loading from '../../../../components/Loading';
import ExtraButtons from '../extraBtns';
import LoadMoreComponent from '../../../../components/LoadMoreComponent';
import { v4 as uuid } from 'uuid';
import { scrollForTable } from '../../../../components/Datatable/utils';
import { getFilters } from '../../../../components/Storage/getFilters';
import { sleep } from '../../../../helpers/sleep';
import { withTranslation } from 'react-i18next';

class KeywordClusters extends LoadMoreComponent {
    constructor(props) {
        super();
        if (!props.data) this.query = query(props.dates);
        this.chooseFilter = this.chooseFilter.bind(this);
        this.callQuery = this.callQuery.bind(this);
        this.checkMaxMin = this.checkMaxMin.bind(this);

        this.offsetStep = 1000;
        this.url = new URL(window.location);
    }

    searchFunction(data, search) {
        if (!search) return data;
        return data.filter((item) =>
            item.cluster_name?.toLowerCase().includes(search.toLowerCase()),
        );
    }

    async callQuery() {
        const params = new URL(document.location).searchParams;
        const filter = params.get('defaultFilter');

        if (filter) {
            this.chooseFilter(filter, true);
        } else {
            this.query.bind(this.setState.bind(this));
            await this.query.update();
        }
    }

    async checkMaxMin() {
        if (!window.waitMinMax) return this.callQuery();

        if (!window.minMaxDates) {
            await sleep(1000);
            return this.checkMaxMin();
        }

        this.callQuery();
    }

    async componentDidMount() {
        await sleep(0);
        this.checkMaxMin();
    }

    componentDidUpdate(prevProps, prevState) {
        const filters = getFilters();
        const data = this.state.data?.map((x) => ({ id: uuid(), ...x }));

        if (prevState.filters !== filters && !this.state.loading) {
            return this.setState({ allData: data, filters });
        }
        if (
            JSON.stringify(this.state.data) !== JSON.stringify(prevState.data) &&
            !this.state.loading
        ) {
            this.setState({ allData: [...this.state.allData, ...data] });
        }

        if (JSON.stringify(prevProps.dates) !== JSON.stringify(this.props.dates)) {
            this.props.updateDates({ key: 'keywordClusters', value: null });
            this.setState({ loading: true, data: null });
            this.query = query({ ...this.props.dates, filter: this.state.filter });
            this.query.bind(this.setState.bind(this));
            this.query.update();
            this.props.updateDates({ emptyAll: true });
        }
        if (
            this.state.allData &&
            JSON.stringify(this.state.allData) !== JSON.stringify(prevState.allData)
        ) {
            this.props.updateDates({ key: 'keywordClusters', value: this.state.allData });
        }
    }

    chooseFilter(filter, fromMount = false) {
        if (this.state.filter === filter) return;
        if (filter) {
            this.url.searchParams.set('defaultFilter', filter);
        } else {
            this.url.searchParams.delete('defaultFilter');
        }
        history.pushState({}, '', this.url);
        this.setState({
            filter,
            allData: [],
            loading: true,
        });
        if (!fromMount) this.props.updateDates({ key: 'keywordClusters', value: null });
        this.query = query({ ...this.props.dates, filter });
        this.query.bind(this.setState.bind(this));
        this.query.update();
    }

    render() {
        const { t } = this.props;

        const data = this.state.allData?.length ? this.state.allData : this.props.data;
        if (
            (!data || !data?.length) &&
            this.state.loading &&
            (!this.props.data || !this.props.data.length)
        )
            return <Loading padding={10} />;

        const attr = {
            query,
            ...this.props.dates,
            filter: this.state.filter ?? null,
            offsetStep: this.offsetStep,
        };

        const sortFns = {
            cluster_name: (array) =>
                array.sort((a, b) => a.cluster_name.localeCompare(b.cluster_name)),
            impressions: (array) => array.sort((a, b) => a.impressions - b.impressions),
            avgCTR: (array) =>
                array.sort((a, b) => a.clicks / a.impressions - b.clicks / b.impressions),
            avgPosition: (array) => array.sort((a, b) => a.avgPosition - b.avgPosition),
            clicks: (array) => array.sort((a, b) => a.clicks - b.clicks),
            impressions_delta: (array) =>
                array.sort(
                    (a, b) =>
                        getDifference(a.impressions, a.impressionsPrior) -
                        getDifference(b.impressions, b.impressionsPrior),
                ),
            clicks_delta: (array) =>
                array.sort(
                    (a, b) =>
                        getDifference(a.clicks, a.clicksPrior) -
                        getDifference(b.clicks, b.clicksPrior),
                ),
            avgPosition_delta: (array) =>
                array.sort(
                    (a, b) =>
                        getDifference(a.avgPosition, a.avgPositionPrior) -
                        getDifference(b.avgPosition, b.avgPositionPrior),
                ),
            avgCTR_delta: (array) =>
                array.sort(
                    (a, b) =>
                        getDifference(a.avgCTR, a.avgCTRPrior) -
                        getDifference(b.avgCTR, b.avgCTRPrior),
                ),
        };
        const showLoadMoreBtn = this.state.data?.length === this.offsetStep;

        return (
            <>
                <DataTable
                    nodes={data ?? []}
                    sortFns={sortFns}
                    COLUMNS={COLUMNS(t)}
                    fileName={t('search.comparison.keywordClusers.header')}
                    header={t('search.comparison.keywordClusers.header')}
                    defaultSortKey="clicks"
                    searchFunction={this.searchFunction}
                    forceRun={this.forceRun}
                    showLoadMoreBtn={showLoadMoreBtn}
                    loadMoreLoading={this.state.loadMore}
                    loadMore={() => this.loadMore(attr)}
                    extraBtns={() => (
                        <ExtraButtons filter={this.state.filter} chooseFilter={this.chooseFilter} />
                    )}
                    additionalStyles={{
                        Table: `
                                --data-table-library_grid-template-columns : minmax(120px, 2fr) minmax(120px, 1fr) minmax(180px, 1.8fr) minmax(120px, 1fr) minmax(180px, 1.8fr) minmax(120px, 1fr) minmax(180px, 1.8fr) minmax(120px, 1fr) minmax(180px, 1.8fr) !important;
                               ${scrollForTable}
                            `,
                        BaseCell: ` font-family: 'Raleway';
                                        font-weight: 500;
                                        font-size: 16px;
                                        line-height: 19px;
                                        padding: 16px 10px;
                                        color: #0D182C;
                                    
                                    
                                        &.underline {
                                        text-decoration: underline;
                                        }`,
                    }}
                />
            </>
        );
    }
}

export default withTranslation()(KeywordClusters);
